import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CurrencyInput } from '../components/CurrencyInput';
import { formatApiError } from '../utils/apiError';
import { PageHero } from '../components/motion/PageHero';
import { listRules as listCmcRules } from '../services/cmcApi';
import { Plus, MagnifyingGlass, Funnel, Eye, PencilSimple, Folder, Warning, CheckCircle, Clock, FilePdf, ListChecks, PencilLine, FileDashed, Envelope, FileText, ShieldCheck, PauseCircle, XCircle, Archive, Flame, ThermometerHot, Snowflake, Cloud, Users, Handshake, ShieldStar, HouseLine, Wrench, HardHat, BookOpen, Bank, Scales, Buildings, Handshake as HandshakeAlt, Download } from '@phosphor-icons/react';
import { getDossiers, createDossier, updateDossier, getOrganizations, getContacts, updateChecklistItem, generateDevisPdf, generateAttestationPdf, signDossier, exportDossiers, getDossierHistory } from '../services/api';
import apiInstance from '../services/api';
import { Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import { SignaturePad } from '../components/SignaturePad';
import { DataTable } from '../components/DataTable';
import { Flag } from '../components/Flag';
import FormationDocsPanel from '../components/FormationDocsPanel';
import DossierPaymentsPanel from '../components/DossierPaymentsPanel';
import { TERRITOIRES, findTerritoire } from '../constants/countries';
import { AIAssistButton } from '../components/AIAssistButton';
import { generateDossierSummaryAI, generateFullDossierAI, listMissionTemplates } from '../services/api';
import { MISSION_TEMPLATES as FALLBACK_TEMPLATES } from '../constants/missionTemplates';
import { Sparkle, MagicWand } from '@phosphor-icons/react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { Dialog as MiniDialog, DialogContent as MiniDialogContent, DialogHeader as MiniDialogHeader, DialogTitle as MiniDialogTitle, DialogFooter as MiniDialogFooter } from '../components/ui/dialog';

const typeDossierOptions = [
  { value: 'adhesion_ftpe',            label: 'Adhésion FTPE',                Icon: Users,       iconColor: '#2D5A4C' },
  { value: 'accompagnement_cooperatif', label: 'Accompagnement coopératif',   Icon: Handshake,   iconColor: '#2D5A4C' },
  { value: 'mission_coop_cipaesse',    label: "Mission coopérative C.I.P.A.E.S.S.E (O'SCOP)", Icon: Handshake, iconColor: '#0891B2', isMission: true },
  { value: 'mission_coop_cipascop',    label: 'Mission coopérative C.I.P.A.S.C.O.P (Coopérateur PM)', Icon: Handshake, iconColor: '#7C3AED', isMission: true },
  { value: 'copsecur',                 label: 'COPSECUR',                     Icon: ShieldStar,  iconColor: '#7C3AED' },
  { value: 'rcds',                     label: 'RCDS',                         Icon: HouseLine,   iconColor: '#0EA5E9' },
  { value: 'domium',                   label: 'DOMIUM',                       Icon: Buildings,   iconColor: '#EA580C' },
  { value: 'archos',                   label: 'ARCHOS',                       Icon: Wrench,      iconColor: '#B45309' },
  { value: 'cap_bati',                 label: 'CAP BÂTI',                     Icon: HardHat,     iconColor: '#DC2626' },
  { value: 'partenariat',              label: 'Partenariat institutionnel',   Icon: HandshakeAlt,iconColor: '#059669' },
  { value: 'formation',                label: 'Formation',                    Icon: BookOpen,    iconColor: '#7C3AED' },
  { value: 'adhesion_formation',       label: "Adhésion centre de formation (FORMA'SCOP)", Icon: BookOpen, iconColor: '#7C3AED', isFormation: true },
  { value: 'salle_formation',          label: 'Convention salle de formation',Icon: BookOpen,    iconColor: '#B45309', isFormation: true },
  { value: 'convention_cadre_formascop', label: "Convention-cadre FORMA'SCOP", Icon: Handshake,  iconColor: '#059669', isFormation: true },
  { value: 'financement',              label: 'Financement / Investisseur',   Icon: Bank,        iconColor: '#2563EB' },
  { value: 'mediation',                label: 'Médiation / Contentieux',      Icon: Scales,      iconColor: '#5E6D66' },
];

const findTypeDossier = (v) => typeDossierOptions.find(t => t.value === v);

const statutOptions = [
  { value: 'brouillon',        label: 'Brouillon',         color: 'bg-gray-100 text-gray-800',      Icon: FileDashed,  iconColor: '#6B7280' },
  { value: 'ouvert',           label: 'Ouvert',            color: 'bg-blue-100 text-blue-800',      Icon: Folder,      iconColor: '#2563EB' },
  { value: 'pieces_demandees', label: 'Pièces demandées',  color: 'bg-yellow-100 text-yellow-800',  Icon: Envelope,    iconColor: '#B45309' },
  { value: 'complet',          label: 'Complet',           color: 'bg-emerald-100 text-emerald-800',Icon: CheckCircle, iconColor: '#059669' },
  { value: 'en_analyse',       label: 'En analyse',        color: 'bg-purple-100 text-purple-800',  Icon: MagnifyingGlass, iconColor: '#7C3AED' },
  { value: 'valide',           label: 'Validé',            color: 'bg-green-100 text-green-800',    Icon: CheckCircle, iconColor: '#16A34A' },
  { value: 'active',           label: 'Activé',            color: 'bg-[#2D5A4C] text-white',        Icon: ShieldCheck, iconColor: '#2D5A4C' },
  { value: 'suspendu',         label: 'Suspendu',          color: 'bg-orange-100 text-orange-800',  Icon: PauseCircle, iconColor: '#EA580C' },
  { value: 'refuse',           label: 'Refusé',            color: 'bg-red-100 text-red-800',        Icon: XCircle,     iconColor: '#DC2626' },
  { value: 'cloture',          label: 'Clôturé',           color: 'bg-slate-100 text-slate-800',    Icon: Archive,     iconColor: '#475569' },
];

const urgenceOptions = [
  { value: 'basse',    label: 'Basse',    Icon: Snowflake,      iconColor: '#0EA5E9' },
  { value: 'normale',  label: 'Normale',  Icon: Cloud,          iconColor: '#64748B' },
  { value: 'haute',    label: 'Haute',    Icon: ThermometerHot, iconColor: '#EA580C' },
  { value: 'critique', label: 'Critique', Icon: Flame,          iconColor: '#DC2626' },
];

const findStatut = (v) => statutOptions.find(s => s.value === v);
const findUrgence = (v) => urgenceOptions.find(u => u.value === v);

const territoireOptions = TERRITOIRES;

export function DossiersPage() {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterTerritoire, setFilterTerritoire] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDossier, setEditingDossier] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  // Assistant Rédaction Complète
  const [fullAssistantOpen, setFullAssistantOpen] = useState(false);
  const [assistantIntention, setAssistantIntention] = useState('');
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantResult, setAssistantResult] = useState(null); // {objet, resume_executif, livrables[]}
  const [missionTemplates, setMissionTemplates] = useState(FALLBACK_TEMPLATES);

  useEffect(() => {
    listMissionTemplates().then((r) => {
      if (Array.isArray(r.data?.items) && r.data.items.length) setMissionTemplates(r.data.items);
    }).catch(() => { /* garde le fallback */ });
  }, []);

  const applyMissionTemplate = (tpl) => {
    setFormData((fd) => ({ ...fd, objet_besoin: fd.objet_besoin || tpl.objet, resume_executif: fd.resume_executif || tpl.resume }));
    toast.success(`Modèle « ${tpl.label} » appliqué`);
  };

  const runFullAssistant = async () => {
    if (!assistantIntention || assistantIntention.trim().length < 10) {
      toast.error('Décrivez votre intention en 2 phrases (min 10 caractères)');
      return;
    }
    setAssistantLoading(true);
    try {
      const r = await generateFullDossierAI({
        intention: assistantIntention,
        type_dossier: formData.type_dossier,
        territoire: formData.territoire,
      });
      setAssistantResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de l'assistant IA");
    }
    setAssistantLoading(false);
  };

  const applyFullAssistant = () => {
    if (!assistantResult) return;
    setFormData((fd) => ({
      ...fd,
      objet_besoin: assistantResult.objet,
      resume_executif: assistantResult.resume_executif,
      livrables: (assistantResult.livrables || []).join('\n'),
    }));
    setFullAssistantOpen(false);
    setAssistantIntention('');
    setAssistantResult(null);
    toast.success('Dossier prérempli par l\'IA');
  };

  // Historique du dossier en édition
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detailDossier, setDetailDossier] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [showSignPad, setShowSignPad] = useState(false);
  const [formData, setFormData] = useState({
    type_dossier: '',
    organization_id: '',
    contact_id: '',
    produit_dispositif: '',
    territoire: '',
    niveau_urgence: 'normale',
    objet_besoin: '',
    resume_executif: '',
    statut: 'brouillon',
    // Mission coopérative
    mission_intitule: '',
    mission_descriptif: '',
    mission_lieu: '',
    mission_date_debut: '',
    mission_date_fin: '',
    mission_competences: '',
    mission_qualification: '',
    mission_paiement_previsionnel: '',
    mission_cmc_rule_ids: [],
  });
  const [cmcRules, setCmcRules] = useState([]);
  const [pricingResult, setPricingResult] = useState(null);
  const [computingPricing, setComputingPricing] = useState(false);

  const fetchData = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterType) params.type_dossier = filterType;
      if (filterStatut) params.statut = filterStatut;
      if (filterTerritoire) params.territoire = filterTerritoire;
      
      const [dossiersRes, orgsRes, contactsRes] = await Promise.all([
        getDossiers(params),
        getOrganizations(),
        getContacts()
      ]);
      setDossiers(dossiersRes.data.items || []);
      setOrganizations(orgsRes.data.items || []);
      setContacts(contactsRes.data.items || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [search, filterType, filterStatut, filterTerritoire]);

  useEffect(() => {
    // Charger les règles CMC pour la liaison des missions
    listCmcRules({ limit: 200 })
      .then((r) => setCmcRules(r.data.items || []))
      .catch(() => setCmcRules([]));
  }, []);

  const handleExport = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterType) params.type_dossier = filterType;
      if (filterStatut) params.statut = filterStatut;
      if (filterTerritoire) params.territoire = filterTerritoire;
      const res = await exportDossiers(params);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dossiers_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch (e) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDossier) {
        await updateDossier(editingDossier.id, formData);
        toast.success('Dossier mis à jour');
      } else {
        await createDossier(formData);
        toast.success('Dossier créé');
      }
      setDialogOpen(false);
      setEditingDossier(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const resetForm = () => {
    setFormData({
      type_dossier: '',
      organization_id: '',
      contact_id: '',
      produit_dispositif: '',
      territoire: '',
      niveau_urgence: 'normale',
      objet_besoin: '',
      resume_executif: '',
      statut: 'brouillon',
      mission_intitule: '',
      mission_descriptif: '',
      mission_lieu: '',
      mission_date_debut: '',
      mission_date_fin: '',
      mission_competences: '',
      mission_qualification: '',
      mission_paiement_previsionnel: '',
      mission_cmc_rule_ids: [],
    });
  };

  const handleEdit = (dossier) => {
    setEditingDossier(dossier);
    setFormData({
      type_dossier: dossier.type_dossier || '',
      organization_id: dossier.organization_id || '',
      contact_id: dossier.contact_id || '',
      produit_dispositif: dossier.produit_dispositif || '',
      territoire: dossier.territoire || '',
      niveau_urgence: dossier.niveau_urgence || 'normale',
      objet_besoin: dossier.objet_besoin || '',
      resume_executif: dossier.resume_executif || '',
      statut: dossier.statut || 'brouillon',
      mission_intitule: dossier.mission_intitule || '',
      mission_descriptif: dossier.mission_descriptif || '',
      mission_lieu: dossier.mission_lieu || '',
      mission_date_debut: dossier.mission_date_debut || '',
      mission_date_fin: dossier.mission_date_fin || '',
      mission_competences: dossier.mission_competences || '',
      mission_qualification: dossier.mission_qualification || '',
      mission_paiement_previsionnel: dossier.mission_paiement_previsionnel || '',
      mission_cmc_rule_ids: dossier.mission_cmc_rule_ids || [],
    });
    setDialogOpen(true);
    // Charger l'historique des changements de statut
    setHistory([]);
    setHistoryLoading(true);
    getDossierHistory(dossier.id)
      .then((r) => setHistory(r.data.items || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  };

  const openNewDialog = () => {
    setEditingDossier(null);
    resetForm();
    setDialogOpen(true);
  };

  // Keyboard shortcut: 'n' opens new dossier dialog
  useEffect(() => {
    const handleNew = () => { setEditingDossier(null); resetForm(); setDialogOpen(true); };
    window.addEventListener('crm-shortcut-new', handleNew);
    return () => window.removeEventListener('crm-shortcut-new', handleNew);
  }, []);

  const getOrgName = (id) => organizations.find(o => o.id === id)?.raison_sociale || '-';
  const getStatutBadge = (statut) => {
    const s = statutOptions.find(opt => opt.value === statut);
    if (!s) return <Badge>{statut}</Badge>;
    const I = s.Icon;
    return <Badge className={`${s.color} inline-flex items-center gap-1`}><I size={12} weight="fill" /><span>{s.label}</span></Badge>;
  };

  const getUrgenceIcon = (urgence) => {
    const u = findUrgence(urgence);
    if (!u) return null;
    const I = u.Icon;
    return <I size={16} color={u.iconColor} weight="fill" />;
  };

  const handleViewDetail = (dossier) => {
    setDetailDossier(dossier);
    setDetailOpen(true);
  };

  const handleChecklistToggle = async (dossier, key, currentValue) => {
    try {
      const res = await updateChecklistItem(dossier.id, { document_key: key, checked: !currentValue });
      setDetailDossier(res.data);
      // Update in list too
      setDossiers(prev => prev.map(d => d.id === dossier.id ? res.data : d));
      toast.success('Checklist mise a jour');
    } catch {
      toast.error('Erreur de mise a jour');
    }
  };

  const handleDownloadPdf = async (type, id) => {
    try {
      setPdfLoading(type);
      let res;
      if (type === 'devis') res = await generateDevisPdf(id);
      else res = await generateAttestationPdf(id);
      
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${id.slice(0,8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF ${type} telecharge`);
    } catch {
      toast.error('Erreur lors de la generation du PDF');
    } finally {
      setPdfLoading(null);
    }
  };

  const getChecklistProgress = (checklist) => {
    if (!checklist || checklist.length === 0) return null;
    const checked = checklist.filter(c => c.checked).length;
    return { checked, total: checklist.length, percent: Math.round((checked / checklist.length) * 100) };
  };

  const handleSignDossier = async (signatureDataUrl) => {
    if (!detailDossier) return;
    try {
      const res = await signDossier(detailDossier.id, { signature: signatureDataUrl });
      setDetailDossier(res.data);
      setDossiers(prev => prev.map(d => d.id === detailDossier.id ? res.data : d));
      setShowSignPad(false);
      toast.success('Dossier signe avec succes');
    } catch {
      toast.error('Erreur lors de la signature');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Dossiers ESS"
        subtitle="Missions coopératives, adhésions et projets accompagnés par O'SCOP"
        icon={<Folder size={24} weight="duotone" className="text-white" />}
        gradient="from-[#D5007D] to-[#EA3C3A]"
        accents={['#FF8E00', '#80C51F']}
        actions={(
          <>
            <Button variant="outline" onClick={handleExport} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="export-dossiers-btn">
              <Download size={18} className="mr-2" /> Exporter
            </Button>
            <Button onClick={openNewDialog} className="bg-white text-[#D5007D] hover:bg-white/90 font-bold" data-testid="new-dossier-btn">
              <Plus size={18} className="mr-2" />
              Nouveau
            </Button>
          </>
        )}
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Folder size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2421]">{dossiers.filter(d => d.statut === 'ouvert').length}</p>
              <p className="text-xs text-[#5E6D66]">Ouverts</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock size={20} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2421]">{dossiers.filter(d => d.statut === 'en_analyse').length}</p>
              <p className="text-xs text-[#5E6D66]">En analyse</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2421]">{dossiers.filter(d => d.statut === 'active').length}</p>
              <p className="text-xs text-[#5E6D66]">Activés</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Warning size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2421]">{dossiers.filter(d => d.statut === 'suspendu').length}</p>
              <p className="text-xs text-[#5E6D66]">Suspendus</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
              <Input
                type="search"
                placeholder="Rechercher par numéro, objet..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-[#E8E6E1]"
                data-testid="dossier-search"
              />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="border-[#E8E6E1]">
              <Funnel size={18} className="mr-2" />
              Filtres
            </Button>
          </div>

          {showFilters && (
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#E8E6E1]">
              <Select value={filterType} onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[200px] border-[#E8E6E1]">
                  <SelectValue placeholder="Type de dossier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {typeDossierOptions.map(t => { const I = t.Icon; return (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="flex items-center gap-2"><I size={16} color={t.iconColor} weight="fill" /><span>{t.label}</span></span>
                    </SelectItem>
                  );})}
                </SelectContent>
              </Select>

              <Select value={filterStatut} onValueChange={(v) => setFilterStatut(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[180px] border-[#E8E6E1]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {statutOptions.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterTerritoire} onValueChange={(v) => setFilterTerritoire(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[180px] border-[#E8E6E1]">
                  <SelectValue placeholder="Territoire" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {territoireOptions.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="flex items-center gap-2"><Flag iso={t.iso} size={16} /><span>{t.label}</span></span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dossiers Table */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
            </div>
          ) : dossiers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#5E6D66]">Aucun dossier trouve</p>
              <Button onClick={openNewDialog} variant="link" className="text-[#2D5A4C] mt-2">
                Creer votre premier dossier
              </Button>
            </div>
          ) : (
            <DataTable
              data={dossiers}
              columns={[
                {
                  key: 'numero_dossier',
                  label: 'N Dossier',
                  render: (val) => (
                    <div className="flex items-center gap-2">
                      <Folder size={16} className="text-[#2D5A4C]" />
                      <span className="font-mono text-sm font-medium">{val}</span>
                    </div>
                  ),
                },
                {
                  key: 'type_dossier',
                  label: 'Type',
                  render: (val) => {
                    const t = findTypeDossier(val);
                    if (!t) return <Badge variant="outline" className="text-xs">{val}</Badge>;
                    const I = t.Icon;
                    return <Badge variant="outline" className="text-xs inline-flex items-center gap-1"><I size={12} color={t.iconColor} weight="fill" /><span>{t.label}</span></Badge>;
                  },
                },
                {
                  key: 'organization_id',
                  label: 'Organisation',
                  render: (val) => <span className="text-sm text-[#1A2421]">{getOrgName(val)}</span>,
                },
                {
                  key: 'territoire',
                  label: 'Territoire',
                  render: (val) => <span className="text-sm text-[#5E6D66]">{val || '-'}</span>,
                },
                {
                  key: 'statut',
                  label: 'Statut',
                  render: (val) => getStatutBadge(val),
                },
                {
                  key: 'niveau_urgence',
                  label: 'Urgence',
                  render: (val) => (
                    <div className="flex items-center gap-1">
                      {getUrgenceIcon(val)}
                      <span className="text-sm text-[#5E6D66] capitalize">{val}</span>
                    </div>
                  ),
                },
                {
                  key: 'checklist',
                  label: 'Checklist',
                  sortable: false,
                  render: (val) => {
                    const progress = getChecklistProgress(val);
                    if (!progress) return <span className="text-xs text-[#5E6D66]">-</span>;
                    return (
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-[#E8E6E1] rounded-full overflow-hidden">
                          <div className="h-full bg-[#2D5A4C] rounded-full transition-all" style={{ width: `${progress.percent}%` }} />
                        </div>
                        <span className="text-xs text-[#5E6D66]">{progress.checked}/{progress.total}</span>
                      </div>
                    );
                  },
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  sortable: false,
                  className: 'text-right',
                  cellClassName: 'text-right',
                  render: (_, row) => (
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => handleViewDetail(row)} data-testid={`view-dossier-${row.id}`}>
                        <Eye size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(row)} data-testid={`edit-dossier-${row.id}`}>
                        <PencilSimple size={16} />
                      </Button>
                    </div>
                  ),
                },
              ]}
              searchKeys={['numero_dossier', 'objet_besoin', 'territoire']}
              showSearch={false}
              emptyMessage="Aucun dossier trouve"
              data-testid="dossiers-datatable"
              mobileCardRender={(row) => (
                <Card className="border-[#E8E6E1] hover:border-[#2D5A4C]/30 transition-colors active:bg-[#F9F8F5]">
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <Folder size={14} className="text-[#2D5A4C] flex-shrink-0" />
                          <span className="font-mono text-sm font-semibold text-[#1A2421]">{row.numero_dossier}</span>
                        </div>
                        <p className="text-[11px] text-[#5E6D66] mt-0.5 truncate">{getOrgName(row.organization_id)}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 ml-2 flex-shrink-0">
                        {getStatutBadge(row.statut)}
                        <Badge variant="outline" className="text-[9px]">{typeDossierOptions.find(t => t.value === row.type_dossier)?.label || row.type_dossier}</Badge>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {row.territoire && <span className="text-[10px] text-[#5E6D66]">{row.territoire}</span>}
                        <div className="flex items-center gap-1">{getUrgenceIcon(row.niveau_urgence)}<span className="text-[10px] text-[#5E6D66] capitalize">{row.niveau_urgence}</span></div>
                      </div>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleViewDetail(row)}>Voir</Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleEdit(row)}>Modifier</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            />
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <DialogTitle style={{ fontFamily: 'Manrope' }}>
                {editingDossier ? 'Modifier le dossier' : 'Nouveau dossier'}
              </DialogTitle>
              {!editingDossier && (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => { setAssistantIntention(''); setAssistantResult(null); setFullAssistantOpen(true); }}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-gradient-to-r from-[#2D5A4C] to-[#00AEEF] hover:opacity-90 px-2.5 py-1 rounded transition-opacity"
                    data-testid="dossier-full-assistant"
                    title="Générer un dossier complet à partir d'une intention en 2 phrases"
                  >
                    <MagicWand size={12} weight="fill" /> Assistant complet
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-[#2D5A4C] hover:text-[#1E4136] hover:bg-[#2D5A4C]/10 px-2 py-1 rounded transition-colors border border-[#2D5A4C]/20"
                        data-testid="dossier-templates"
                        title="Modèles de missions prêts à personnaliser"
                      >
                        <BookOpen size={12} weight="bold" /> Modèles
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#5E6D66]">Modèles de missions</DropdownMenuLabel>
                      {missionTemplates.map((t) => (
                        <DropdownMenuItem key={t.id} onClick={() => applyMissionTemplate(t)} data-testid={`template-${t.id}`}>
                          <span className="mr-1">{t.icon || '📄'}</span> {t.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de dossier *</Label>
                <Select value={formData.type_dossier} onValueChange={(v) => setFormData({ ...formData, type_dossier: v })} required>
                  <SelectTrigger data-testid="select-type-dossier">
                    <SelectValue placeholder="Sélectionner">
                      {(() => { const t = findTypeDossier(formData.type_dossier); if (!t) return null; const I = t.Icon; return (
                        <span className="flex items-center gap-2"><I size={16} color={t.iconColor} weight="fill" /><span>{t.label}</span></span>
                      );})()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {typeDossierOptions.map(t => { const I = t.Icon; return (
                      <SelectItem key={t.value} value={t.value}>
                        <span className="flex items-center gap-2"><I size={16} color={t.iconColor} weight="fill" /><span>{t.label}</span></span>
                      </SelectItem>
                    );})}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Organisation porteuse *</Label>
                <Select value={formData.organization_id} onValueChange={(v) => setFormData({ ...formData, organization_id: v })} required>
                  <SelectTrigger data-testid="select-organization">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.raison_sociale}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact principal</Label>
                <Select value={formData.contact_id} onValueChange={(v) => setFormData({ ...formData, contact_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {contacts.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.prenom} {c.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Territoire</Label>
                <Select value={formData.territoire} onValueChange={(v) => setFormData({ ...formData, territoire: v })}>
                  <SelectTrigger data-testid="select-dossier-territoire">
                    <SelectValue placeholder="Sélectionner un territoire">
                      {formData.territoire ? (
                        <span className="flex items-center gap-2">
                          <Flag iso={findTerritoire(formData.territoire)?.iso} size={16} />
                          <span>{formData.territoire}</span>
                        </span>
                      ) : null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    {territoireOptions.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        <span className="flex items-center gap-2">
                          <Flag iso={t.iso} size={16} />
                          <span>{t.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Niveau d'urgence</Label>
                <Select value={formData.niveau_urgence} onValueChange={(v) => setFormData({ ...formData, niveau_urgence: v })}>
                  <SelectTrigger data-testid="select-dossier-urgence">
                    <SelectValue>
                      {(() => { const u = findUrgence(formData.niveau_urgence); if (!u) return null; const I = u.Icon; return (
                        <span className="flex items-center gap-2"><I size={16} color={u.iconColor} weight="fill" /><span>{u.label}</span></span>
                      );})()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {urgenceOptions.map(u => { const I = u.Icon; return (
                      <SelectItem key={u.value} value={u.value}>
                        <span className="flex items-center gap-2"><I size={16} color={u.iconColor} weight="fill" /><span>{u.label}</span></span>
                      </SelectItem>
                    );})}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={formData.statut} onValueChange={(v) => setFormData({ ...formData, statut: v })}>
                  <SelectTrigger data-testid="select-dossier-statut">
                    <SelectValue>
                      {(() => { const s = findStatut(formData.statut); if (!s) return null; const I = s.Icon; return (
                        <span className="flex items-center gap-2"><I size={16} color={s.iconColor} weight="fill" /><span>{s.label}</span></span>
                      );})()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {statutOptions.map(s => { const I = s.Icon; return (
                      <SelectItem key={s.value} value={s.value}>
                        <span className="flex items-center gap-2"><I size={16} color={s.iconColor} weight="fill" /><span>{s.label}</span></span>
                      </SelectItem>
                    );})}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Objet du besoin</Label>
                <AIAssistButton
                  value={formData.objet_besoin}
                  field="Objet du besoin d'un dossier ESS"
                  onApply={(t) => setFormData({ ...formData, objet_besoin: t })}
                  testId="ai-assist-objet"
                />
              </div>
              <Input
                value={formData.objet_besoin}
                onChange={(e) => setFormData({ ...formData, objet_besoin: e.target.value })}
                placeholder="Description courte du besoin"
                data-testid="dossier-objet"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Résumé exécutif</Label>
                <div className="flex items-center gap-1.5">
                  {editingDossier && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          setSummaryLoading(true);
                          const r = await generateDossierSummaryAI({ dossier_id: editingDossier.id });
                          setFormData((fd) => ({ ...fd, resume_executif: r.data.text }));
                          toast.success('Résumé exécutif généré');
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Impossible de générer le résumé");
                        }
                        setSummaryLoading(false);
                      }}
                      disabled={summaryLoading}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-white bg-[#2D5A4C] hover:bg-[#1E4136] px-2 py-0.5 rounded transition-colors disabled:opacity-50"
                      data-testid="dossier-summary-ai"
                      title="Générer un résumé exécutif à partir des données du dossier"
                    >
                      {summaryLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
                      Résumé IA
                    </button>
                  )}
                  <AIAssistButton
                    value={formData.resume_executif}
                    field="Résumé exécutif d'un dossier ESS"
                    onApply={(t) => setFormData({ ...formData, resume_executif: t })}
                    testId="ai-assist-resume"
                  />
                </div>
              </div>
              <Textarea
                value={formData.resume_executif}
                onChange={(e) => setFormData({ ...formData, resume_executif: e.target.value })}
                rows={3}
                placeholder="Contexte, enjeux, objectifs..."
                data-testid="dossier-resume"
              />
            </div>

            {/* Champs mission coopérative (CIPAESSE / CIPASCOP) */}
            {findTypeDossier(formData.type_dossier)?.isMission && (
              <div className="space-y-3 pt-3 border-t border-[#E8E6E1]" data-testid="mission-fields">
                <Label className="text-sm font-semibold text-[#1A2421]">Détails de la mission coopérative</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Intitulé de la mission *</Label>
                    <Input
                      value={formData.mission_intitule}
                      onChange={(e) => setFormData({ ...formData, mission_intitule: e.target.value })}
                      placeholder="Titre court de la mission"
                      data-testid="mission-intitule"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Lieu</Label>
                    <Input
                      value={formData.mission_lieu}
                      onChange={(e) => setFormData({ ...formData, mission_lieu: e.target.value })}
                      placeholder="Ville, territoire, à distance…"
                      data-testid="mission-lieu"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Bref descriptif</Label>
                  <Textarea
                    value={formData.mission_descriptif}
                    onChange={(e) => setFormData({ ...formData, mission_descriptif: e.target.value })}
                    rows={2}
                    placeholder="Objet de la mission, livrables, contexte…"
                    data-testid="mission-descriptif"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Période — Date début</Label>
                    <Input
                      type="date"
                      value={formData.mission_date_debut}
                      onChange={(e) => setFormData({ ...formData, mission_date_debut: e.target.value })}
                      data-testid="mission-date-debut"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Période — Date fin</Label>
                    <Input
                      type="date"
                      value={formData.mission_date_fin}
                      onChange={(e) => setFormData({ ...formData, mission_date_fin: e.target.value })}
                      data-testid="mission-date-fin"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Compétences requises</Label>
                    <Input
                      value={formData.mission_competences}
                      onChange={(e) => setFormData({ ...formData, mission_competences: e.target.value })}
                      placeholder="Ex : gestion de projet, ingénierie coopérative…"
                      data-testid="mission-competences"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Qualification</Label>
                    <Input
                      value={formData.mission_qualification}
                      onChange={(e) => setFormData({ ...formData, mission_qualification: e.target.value })}
                      placeholder="Ex : expert, chargé de mission, formateur…"
                      data-testid="mission-qualification"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Paiement coopératif prévisionnel au titre de la mission</Label>
                  <CurrencyInput
                    value={formData.mission_paiement_previsionnel}
                    onChange={(v) => setFormData({ ...formData, mission_paiement_previsionnel: v })}
                    placeholder="0,00"
                    testId="mission-paiement-previsionnel"
                  />
                </div>

                {/* Règles CMC applicables */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Règles CMC applicables à la mission</Label>
                    {(formData.mission_cmc_rule_ids || []).length > 0 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          setComputingPricing(true);
                          try {
                            const base = parseFloat(formData.mission_paiement_previsionnel || 0);
                            const res = await apiInstance.post('/dossiers/mission/pricing', {
                              rule_ids: formData.mission_cmc_rule_ids,
                              base_amount: base,
                              days: 1,
                            });
                            setPricingResult(res.data);
                            if (res.data.total > 0) {
                              setFormData({ ...formData, mission_paiement_previsionnel: res.data.total });
                              toast.success(`Total calculé : ${res.data.total.toFixed(2)} €`);
                            } else {
                              toast.info('Aucun calcul possible (règles sans taux)');
                            }
                          } catch (e) {
                            toast.error(formatApiError(e));
                          }
                          setComputingPricing(false);
                        }}
                        className="text-xs h-7"
                        disabled={computingPricing}
                        data-testid="compute-mission-pricing"
                      >
                        {computingPricing ? '…' : 'Calculer auto.'}
                      </Button>
                    )}
                  </div>
                  {cmcRules.length === 0 ? (
                    <p className="text-[10px] text-[#5E6D66]">Aucune règle CMC disponible. Créez-en dans « CMC O'SCOP → Règles CMC ».</p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-[#E8E6E1] rounded p-2 space-y-1 bg-white" data-testid="mission-cmc-rules">
                      {cmcRules.map((r) => {
                        const selected = (formData.mission_cmc_rule_ids || []).includes(r.id);
                        return (
                          <label key={r.id} className="flex items-start gap-2 text-xs cursor-pointer hover:bg-[#F9F8F5] p-1 rounded">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => {
                                const list = formData.mission_cmc_rule_ids || [];
                                setFormData({
                                  ...formData,
                                  mission_cmc_rule_ids: selected
                                    ? list.filter((x) => x !== r.id)
                                    : [...list, r.id],
                                });
                              }}
                              data-testid={`mission-cmc-rule-${r.id}`}
                              className="mt-0.5"
                            />
                            <div className="flex-1">
                              <span className="font-medium">{r.qualification_label || r.qualification_key || r.family}</span>
                              {r.rate != null && (
                                <span className="text-[10px] text-[#5E6D66] ml-1">
                                  ({r.rate_type === 'percentage' ? `${r.rate}%` : `${r.rate} ${r.basis || ''}`})
                                </span>
                              )}
                              {r.cap_amount != null && (
                                <span className="text-[10px] text-[#5E6D66] ml-1">· plafond {r.cap_amount}</span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Breakdown affichage */}
                {pricingResult && pricingResult.breakdown?.length > 0 && (
                  <div className="p-3 bg-[#F0EFEB] rounded border border-[#E8E6E1] space-y-1" data-testid="mission-pricing-breakdown">
                    <p className="text-xs font-semibold text-[#1A2421]">Détail du calcul :</p>
                    {pricingResult.breakdown.map((b) => (
                      <div key={b.rule_id} className="flex items-center justify-between text-[11px]">
                        <span>{b.label} <span className="text-[#5E6D66]">({b.rate_type}, taux {b.rate})</span> {b.capped && <span className="text-orange-600">· plafonné</span>}</span>
                        <span className="font-medium">{b.computed.toFixed(2)} €</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-xs font-bold pt-1 border-t border-[#E8E6E1]">
                      <span>Total prévisionnel</span>
                      <span>{pricingResult.total.toFixed(2)} €</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {editingDossier && (
              <div className="space-y-2 pt-2 border-t border-[#E8E6E1]" data-testid="dossier-history-section">
                <Label className="text-sm font-semibold text-[#1A2421]">Historique du statut</Label>
                {historyLoading ? (
                  <p className="text-xs text-[#5E6D66]">Chargement…</p>
                ) : history.length === 0 ? (
                  <p className="text-xs text-[#5E6D66]" data-testid="dossier-history-empty">Aucun changement de statut enregistré.</p>
                ) : (
                  <ol className="relative border-l-2 border-[#E8E6E1] ml-2 space-y-3 pt-1" data-testid="dossier-history-list">
                    {history.map((h, i) => {
                      const from = findStatut(h.from_status);
                      const to = findStatut(h.to_status);
                      const d = h.changed_at ? new Date(h.changed_at) : null;
                      const dateStr = d ? d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '';
                      const ToIcon = to?.Icon;
                      return (
                        <li key={i} className="ml-4 relative" data-testid={`dossier-history-item-${i}`}>
                          <span
                            className="absolute -left-[22px] top-1 w-3 h-3 rounded-full ring-2 ring-white flex items-center justify-center"
                            style={{ backgroundColor: to?.iconColor || '#6B7280' }}
                          />
                          <div className="flex items-center gap-2 flex-wrap text-xs">
                            {from ? (
                              <Badge variant="outline" className="text-[10px] gap-1">
                                {from.Icon && <from.Icon size={11} color={from.iconColor} />}
                                {from.label}
                              </Badge>
                            ) : (
                              <span className="text-[#5E6D66] text-[10px]">Création</span>
                            )}
                            <span className="text-[#5E6D66]">→</span>
                            <Badge className={`text-[10px] gap-1 ${to?.color || 'bg-gray-100 text-gray-800'}`}>
                              {ToIcon && <ToIcon size={11} />}
                              {to?.label || h.to_status}
                            </Badge>
                            <span className="text-[#5E6D66] tabular-nums">{dateStr}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-dossier-btn">
                {editingDossier ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dossier Detail Dialog with Checklist & PDF */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              <div className="flex items-center gap-3">
                <Folder size={24} className="text-[#2D5A4C]" />
                Dossier {detailDossier?.numero_dossier}
              </div>
            </DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>

          {detailDossier && (
            <div className="space-y-6">
              {/* Info summary */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-[#5E6D66] uppercase">Type</p>
                  <p className="text-sm font-medium">{typeDossierOptions.find(t => t.value === detailDossier.type_dossier)?.label || detailDossier.type_dossier}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5E6D66] uppercase">Organisation</p>
                  <p className="text-sm font-medium">{getOrgName(detailDossier.organization_id)}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5E6D66] uppercase">Statut</p>
                  {getStatutBadge(detailDossier.statut)}
                </div>
                <div>
                  <p className="text-xs text-[#5E6D66] uppercase">Territoire</p>
                  <p className="text-sm">{detailDossier.territoire || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5E6D66] uppercase">Urgence</p>
                  <p className="text-sm capitalize">{detailDossier.niveau_urgence}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5E6D66] uppercase">Cree le</p>
                  <p className="text-sm">{detailDossier.created_at ? new Date(detailDossier.created_at).toLocaleDateString('fr-FR') : '-'}</p>
                </div>
              </div>

              {detailDossier.objet_besoin && (
                <div>
                  <p className="text-xs text-[#5E6D66] uppercase mb-1">Objet du besoin</p>
                  <p className="text-sm">{detailDossier.objet_besoin}</p>
                </div>
              )}

              {/* PDF Generation Buttons */}
              <div className="border-t border-[#E8E6E1] pt-4">
                <h3 className="text-sm font-semibold text-[#1A2421] mb-3 flex items-center gap-2">
                  <FilePdf size={18} className="text-[#D47A62]" />
                  Documents PDF
                </h3>
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPdf('devis', detailDossier.id)}
                    disabled={pdfLoading === 'devis'}
                    data-testid="download-devis-pdf"
                    className="border-[#E8E6E1]"
                  >
                    {pdfLoading === 'devis' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FilePdf size={16} className="mr-2 text-[#D47A62]" />}
                    Generer devis
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPdf('attestation', detailDossier.id)}
                    disabled={pdfLoading === 'attestation'}
                    data-testid="download-attestation-pdf"
                    className="border-[#E8E6E1]"
                  >
                    {pdfLoading === 'attestation' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FilePdf size={16} className="mr-2 text-[#2D5A4C]" />}
                    Generer attestation
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSignPad(!showSignPad)}
                    data-testid="sign-dossier-btn"
                    className="border-[#E8E6E1]"
                  >
                    <PencilLine size={16} className="mr-2 text-[#2D5A4C]" />
                    Signer le dossier
                  </Button>
                </div>

                {/* Signature Pad */}
                {showSignPad && (
                  <div className="mt-4 p-4 border border-[#E8E6E1] rounded-lg bg-[#F9F8F5]">
                    <p className="text-sm font-medium text-[#1A2421] mb-3">Apposer votre signature sur ce dossier :</p>
                    <SignaturePad onSave={handleSignDossier} onCancel={() => setShowSignPad(false)} />
                  </div>
                )}

                {/* Existing signatures */}
                {detailDossier.signatures && detailDossier.signatures.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-[#5E6D66] uppercase tracking-wider">Signatures apposees</p>
                    {detailDossier.signatures.map((sig, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50">
                        <CheckCircle size={18} className="text-emerald-600 flex-shrink-0" weight="fill" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-[#1A2421]">{sig.signer_name}</p>
                          <p className="text-xs text-[#5E6D66]">{sig.signer_role?.replace(/_/g, ' ')} — {sig.signed_at ? new Date(sig.signed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                        </div>
                        <img src={sig.signature} alt="Signature" className="h-10 opacity-80" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Formation Documents Panel */}
              {detailDossier && ['adhesion_formation', 'salle_formation', 'convention_cadre_formascop'].includes(detailDossier.type_dossier) && (
                <FormationDocsPanel dossierId={detailDossier.id} />
              )}

              {/* Payments Panel (Stripe links + Virements) */}
              {detailDossier && <DossierPaymentsPanel dossierId={detailDossier.id} />}

              {/* Document Checklist */}
              {detailDossier.checklist && detailDossier.checklist.length > 0 && (
                <div className="border-t border-[#E8E6E1] pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
                      <ListChecks size={18} className="text-[#2D5A4C]" />
                      Checklist documentaire
                    </h3>
                    {(() => {
                      const progress = getChecklistProgress(detailDossier.checklist);
                      if (!progress) return null;
                      return (
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-[#E8E6E1] rounded-full overflow-hidden">
                            <div className="h-full bg-[#2D5A4C] rounded-full transition-all" style={{ width: `${progress.percent}%` }} />
                          </div>
                          <span className="text-xs font-medium text-[#5E6D66]">{progress.percent}%</span>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="space-y-2">
                    {detailDossier.checklist.map((item) => (
                      <div
                        key={item.key}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          item.checked ? 'bg-emerald-50 border-emerald-200' : item.required ? 'bg-white border-[#E8E6E1]' : 'bg-white border-[#E8E6E1]/50'
                        }`}
                        data-testid={`checklist-item-${item.key}`}
                      >
                        <Checkbox
                          checked={item.checked}
                          onCheckedChange={() => handleChecklistToggle(detailDossier, item.key, item.checked)}
                          className="data-[state=checked]:bg-[#2D5A4C] data-[state=checked]:border-[#2D5A4C]"
                          data-testid={`checklist-check-${item.key}`}
                        />
                        <div className="flex-1">
                          <p className={`text-sm ${item.checked ? 'line-through text-[#5E6D66]' : 'text-[#1A2421]'}`}>
                            {item.label}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {item.required && !item.checked && (
                            <Badge className="bg-red-50 text-red-600 text-[10px]">Obligatoire</Badge>
                          )}
                          {item.checked && <Check size={16} className="text-emerald-600" />}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>Fermer</Button>
            {detailDossier && (
              <Button className="bg-[#2D5A4C] hover:bg-[#1E4136]" onClick={() => { setDetailOpen(false); handleEdit(detailDossier); }}>
                Modifier
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale Assistant Rédaction Complète */}
      {fullAssistantOpen && (
        <MiniDialog open onOpenChange={(o) => { if (!o) setFullAssistantOpen(false); }}>
          <MiniDialogContent className="max-w-xl" data-testid="dossier-full-assistant-dialog">
            <MiniDialogHeader>
              <MiniDialogTitle className="flex items-center gap-2 text-[#1A2421]">
                <MagicWand size={20} className="text-[#2D5A4C]" weight="fill" /> Assistant de rédaction complète
              </MiniDialogTitle>
            </MiniDialogHeader>
            {!assistantResult ? (
              <div className="space-y-3">
                <p className="text-xs text-[#5E6D66]">
                  Exprimez votre intention en 2 phrases. L'IA génère un objet, un résumé exécutif structuré et une liste de livrables.
                </p>
                <Textarea
                  rows={4}
                  autoFocus
                  value={assistantIntention}
                  onChange={(e) => setAssistantIntention(e.target.value)}
                  placeholder="Ex : Une association guadeloupéenne veut créer une recyclerie pour les habitants de Pointe-à-Pitre. Elle a besoin d'un accompagnement pour structurer la coopérative et sécuriser son modèle économique."
                  maxLength={1000}
                  data-testid="assistant-intention"
                />
                <MiniDialogFooter className="gap-2">
                  <Button variant="ghost" onClick={() => setFullAssistantOpen(false)} data-testid="assistant-cancel">Annuler</Button>
                  <Button
                    className="bg-gradient-to-r from-[#2D5A4C] to-[#00AEEF] text-white hover:opacity-90"
                    disabled={assistantLoading || assistantIntention.trim().length < 10}
                    onClick={runFullAssistant}
                    data-testid="assistant-generate"
                  >
                    {assistantLoading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkle size={14} weight="fill" className="mr-1" />}
                    Générer
                  </Button>
                </MiniDialogFooter>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#5E6D66] font-semibold mb-1">Objet</p>
                  <p className="text-sm bg-[#F5F4EF] rounded p-2" data-testid="assistant-out-objet">{assistantResult.objet}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#5E6D66] font-semibold mb-1">Résumé exécutif</p>
                  <p className="text-xs whitespace-pre-wrap bg-emerald-50 border border-emerald-200 rounded p-2 max-h-40 overflow-y-auto" data-testid="assistant-out-resume">
                    {assistantResult.resume_executif}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#5E6D66] font-semibold mb-1">Livrables</p>
                  <ul className="text-xs list-disc pl-5 space-y-0.5" data-testid="assistant-out-livrables">
                    {(assistantResult.livrables || []).map((l, i) => <li key={i}>{l}</li>)}
                  </ul>
                </div>
                <MiniDialogFooter className="gap-2">
                  <Button variant="ghost" onClick={() => setFullAssistantOpen(false)}>Annuler</Button>
                  <Button variant="outline" disabled={assistantLoading} onClick={runFullAssistant} data-testid="assistant-retry">
                    {assistantLoading ? <Loader2 size={14} className="animate-spin mr-1" /> : null}Réessayer
                  </Button>
                  <Button className="bg-[#2D5A4C] hover:bg-[#1E4136]" onClick={applyFullAssistant} data-testid="assistant-apply">
                    Appliquer au dossier
                  </Button>
                </MiniDialogFooter>
              </div>
            )}
          </MiniDialogContent>
        </MiniDialog>
      )}
    </div>
  );
}
